import { useMutation } from '@apollo/client/react'
import {
  chakra,
  Alert,
  Box,
  Button,
  Field,
  HStack,
  Heading,
  IconButton,
  Input,
  InputGroup,
  Link,
  VStack,
  InputProps,
  Center,
  Spinner,
  Text,
  Separator,
  Icon,
} from '@chakra-ui/react'
import { SocialLoginButtons } from '@src/components/sco/Account/UserLoginForm/SocialLoginButtons'
import { ButtonLink } from '@src/components/sco/ButtonLink.tsx'
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers'
import { FormContainer } from '@src/components/sco/FormContainer'
import { Paragraph } from '@src/components/sco/Paragraph.tsx'
import { ChevronRightIcon } from '@src/components/sco/icons'
import { NextLink } from '@src/components/sco/utilities/NextLink'
import { OrDivider } from '@src/components/sco/utilities/OrDivider'
import { LoginPayload, RequestLoginTokenPayload } from '@src/graphql-types'
import { useCurrentUrl } from '@src/hooks/useCurrentUrl'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import { startupsUrl } from '@src/lib/startups-url'
import tracker from '@src/lib/tracker'
import { useRouter, useSearchParams } from 'next/navigation'
import { parseAsString, useQueryState } from 'nuqs'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { HiEye, HiEyeOff } from 'react-icons/hi'
import { LuChevronRight, LuMail } from 'react-icons/lu'

type LoginFormFields = {
  email: string
  password: string
}

type UserLoginFormProps = GenericFormProps<LoginFormFields> & {
  title?: string
  onRegisterClick?: () => void
  returnToHref?: string
  loginMethod?: 'token' | 'password' | string
}

export const LoginTokenSentAlert = (props: {
  containerProps: UserLoginFormProps['containerProps']
  title?: string
  subtitle?: string | React.ReactNode
  onTryAgain: () => void
}) => {
  return (
    <FormContainer {...(props.containerProps || {})}>
      <VStack align={'center'} gap={3} paddingY={4} textAlign={'center'}>
        <Icon asChild fontSize={'2em'}>
          <LuMail />
        </Icon>
        <Heading>{props.title || 'Next Step: Check Your Email'}</Heading>
        <Paragraph>{props.subtitle || 'We have sent an auto-login link to your email address.'}</Paragraph>
        <Separator marginY={'1em'} width={'full'} />
        <Paragraph>Didn't receive it or having trouble?</Paragraph>
        <HStack>
          <Button size={'sm'} variant={'cta'} onClick={() => props.onTryAgain()}>
            Try Again
          </Button>
          <Text>or</Text>
          <Link asChild variant={'underline'}>
            <NextLink href={'/support'}>Contact Support</NextLink>
          </Link>
        </HStack>
      </VStack>
    </FormContainer>
  )
}

export const UserLoginForm = (props: UserLoginFormProps) => {
  const router = useRouter()
  const params = useSearchParams()
  const currentFullUrl = useCurrentUrl()

  const [formErrorMessage, setFormErrorMessage] = useState<string>(null)
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [isRedirecting, setIsRedirecting] = useState(!!params.get('auth'))
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [prompt, setPrompt] = useQueryState('prompt', parseAsString)
  const [loginMethod, setLoginMethod] = useState<UserLoginFormProps['loginMethod']>(
    props.loginMethod || 'token'
  )
  const form = useForm<LoginFormFields>({ mode: 'onSubmit' })
  const { currentUser, refetchCurrentUser } = useCurrentUser()
  const urlSearchParams = useSearchParams()

  useEffect(() => {
    if (urlSearchParams.get('error')) {
      setFormErrorMessage(`External login failed. Please try again.`)
    } else {
      setFormErrorMessage('')
    }
  }, [urlSearchParams.get('error')])

  const [requestLoginToken, requestLoginTokenMutation] = useMutation(gql`
    mutation ($input: RequestLoginTokenInput!) {
      requestLoginToken(input: $input) {
        success
      }
    }
  `)

  const [login, loginMutation] = useMutation(gql`
    mutation ($input: LoginInput!) {
      login(input: $input) {
        user {
          id
        }
        viewer {
          id
          currentUser {
            id
            userId
          }
        }
      }
    }
  `)

  const onRequestLoginTokenSubmit = useCallback(
    (values: LoginFormFields) =>
      processSubmit({
        props,
        values,
        setFormErrorMessage,
        action: async (values: LoginFormFields) => {
          const result = await requestLoginToken({
            variables: {
              input: {
                email: values.email,
                returnToUrl: startupsUrl(props.returnToHref || currentFullUrl || '/account'),
              },
            },
          })

          return result.data?.requestLoginToken
        },
        afterSuccess: async (values: LoginFormFields, result: RequestLoginTokenPayload) => {
          if (result.success) {
            setIsSubmitted(true)
          } else {
            setFormErrorMessage('Login link failed to send.')
          }
        },
      }),
    [props, formErrorMessage]
  )

  const onPasswordLoginSubmit = useCallback(
    (values: LoginFormFields) =>
      processSubmit({
        props,
        values,
        setFormErrorMessage,
        action: async (values: LoginFormFields) => {
          return await login({
            variables: {
              input: {
                email: values.email,
                password: values.password,
              },
            },
          })
        },
        afterSuccess: async (values: LoginFormFields, result: LoginPayload) => {
          await tracker.loggedIn()
          if (props.returnToHref) {
            setIsRedirecting(true)
            await refetchCurrentUser()
            router.push(props.returnToHref)
          } else {
            await refetchCurrentUser()
            if (props.afterSuccess) {
              props.afterSuccess(values, result)
            }
          }
        },
      }),
    [props, setFormErrorMessage]
  )

  useEffect(() => {
    if (params.get('auth')) {
      router.push('/')
    }
  }, [params.get('auth')])

  const onSubmit = loginMethod == 'password' ? onPasswordLoginSubmit : onRequestLoginTokenSubmit

  if (isSubmitted) {
    if (loginMethod === 'token') {
      return (
        <LoginTokenSentAlert containerProps={props.containerProps} onTryAgain={() => setIsSubmitted(false)} />
      )
    }
  }

  return (
    <FormContainer {...(props.containerProps || {})}>
      <HStack align={'center'} justify={'space-between'} marginBottom={props.title && '1em'}>
        {props.title && <Heading size={'lg'}>{props.title}</Heading>}
        {props.onRegisterClick && (
          <Text color={'colorPalette.fg/60'} fontSize={'12px'}>
            {'No account yet?'}{' '}
            <Link color={'white'} marginLeft={'2px'} variant={'underline'} onClick={props.onRegisterClick}>
              Signup
            </Link>
          </Text>
        )}
      </HStack>
      {isRedirecting && (
        <Center padding={'1em'}>
          <Spinner />
        </Center>
      )}
      {!isRedirecting && (
        <VStack asChild align={'stretch'} gap={'1.25em'}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            {currentUser?.isRegistered ? (
              <Box bgColor={'blackAlpha.500'} borderRadius={'md'} paddingY={'2em'} textAlign={'center'}>
                <Box width={'full'}>
                  <Text lineHeight={'tall'} marginBottom={4}>
                    You are logged in as:
                    <br />
                    <chakra.code>{currentUser?.email}</chakra.code>
                  </Text>
                  <VStack gap={2} justify={'center'}>
                    <ButtonLink
                      asChild
                      href={props.returnToHref || '/account'}
                      size={'md'}
                      variant={'outlineGhost'}
                    >
                      {props.returnToHref ? 'Continue' : 'View Account'}
                      <LuChevronRight />
                    </ButtonLink>
                    {prompt === 'login' && (
                      <Button size={'md'} variant={'cta'} onClick={() => setPrompt(null)}>
                        Close
                      </Button>
                    )}
                  </VStack>
                </Box>
              </Box>
            ) : (
              <>
                {formErrorMessage && (
                  <Alert.Root borderRadius={'md'} status={'error'}>
                    <Alert.Indicator />
                    <Box>{formErrorMessage}</Box>
                  </Alert.Root>
                )}

                <Field.Root invalid={!!form.formState.errors.email}>
                  <HStack align={'center'} gap={0} justify={'space-between'} width={'full'}>
                    <Field.Label htmlFor={'email'}>Email address</Field.Label>
                    {loginMethod !== 'password' && (
                      <Link
                        _hover={{ opacity: 1 }}
                        fontSize={'12px'}
                        opacity={0.5}
                        variant={'underline'}
                        onClick={() => setLoginMethod('password')}
                      >
                        Use Password
                      </Link>
                    )}
                  </HStack>
                  <Input
                    id={'email'}
                    name={'email'}
                    placeholder={'you@email.com'}
                    size={'lg'}
                    type={'email'}
                    {...((props.inputProps as InputProps) || {})}
                    {...form.register('email', {
                      required: 'Email is required.',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                  <Field.ErrorText>{form.formState.errors.email?.message}</Field.ErrorText>
                </Field.Root>

                {loginMethod === 'password' && (
                  <Field.Root invalid={!!form.formState.errors.password}>
                    <HStack align={'center'} gap={0} justify={'space-between'} width={'full'}>
                      <Field.Label htmlFor={'password'}>Password</Field.Label>
                      <Box fontSize={'0.75em'} lineHeight={1} paddingBottom={'0.25em'}>
                        <Link
                          asChild
                          _hover={{ opacity: 1 }}
                          display={'inline-block'}
                          opacity={0.5}
                          variant={'underline'}
                        >
                          <NextLink href={'/reset-password'}>Forgot?</NextLink>
                        </Link>
                      </Box>
                    </HStack>
                    <InputGroup
                      endElement={
                        <IconButton
                          _hover={{ opacity: 1 }}
                          aria-label={showPassword ? 'Hide Password' : 'Show Password'}
                          opacity={'0.5'}
                          size={'sm'}
                          variant={'ghost'}
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <HiEyeOff /> : <HiEye />}
                        </IconButton>
                      }
                    >
                      <Input
                        id={'password'}
                        placeholder={'****'}
                        size={'lg'}
                        type={showPassword ? 'text' : 'password'}
                        {...((props.inputProps as InputProps) || {})}
                        {...form.register('password', {
                          required: 'Password is required',
                        })}
                      />
                    </InputGroup>

                    <Field.ErrorText>{form.formState.errors.password?.message}</Field.ErrorText>
                  </Field.Root>
                )}

                <Box paddingTop={'0.5em'} textAlign={'center'}>
                  <Button
                    loading={form.formState.isSubmitting}
                    size={'lg'}
                    type={'submit'}
                    variant={'cta'}
                    width={'full'}
                    {...(props.submitButtonProps || {})}
                  >
                    {loginMethod === 'password' ? 'Login with Password' : 'Email Me a Login Link'}
                    <ChevronRightIcon />
                  </Button>
                </Box>

                <OrDivider />

                <SocialLoginButtons
                  actionLabel={'Login'}
                  buttonLayout={'google-callout-grid'}
                  returnToHref={props.returnToHref}
                />

                {false && process.env.NEXT_PUBLIC_DEPLOYMENT_ENV !== 'production' && (
                  <Box bgColor={'blackAlpha.500'} borderRadius={'md'} marginTop={'1em'} padding={'1em'}>
                    <Text fontFamily={'monospace'} mb={4} textAlign={'center'}>
                      DEVELOPMENT - QUICK LOGIN
                    </Text>
                    <VStack gap={2}>
                      {[
                        'test+admin@startups.com',
                        'test+sales@startups.com',
                        'test+advisor@startups.com',
                        'test+user@startups.com',
                        'test+free@startups.com',
                      ].map((email, index) => (
                        <Link
                          key={index}
                          variant={'underline'}
                          onClick={async () => {
                            setLoginMethod('password')
                            form.setValue('email', email)
                            form.setValue('password', email)
                            await form.handleSubmit(onPasswordLoginSubmit)()
                          }}
                        >
                          {email}
                        </Link>
                      ))}
                    </VStack>
                  </Box>
                )}
              </>
            )}
          </form>
        </VStack>
      )}
    </FormContainer>
  )
}
